import "./navbar.scss";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { ListItemText, Menu, MenuItem } from '@mui/material';
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../store/AuthContext";
import UserDropdown from "../userDropdown/UserDropdown";
import { useSidebarState } from '../sidebar/SidebarStateContext';
import { toggleSidebar } from '../sidebar/sidebarActions';
import { db } from "../../firebase";
import { collection, getDocs, onSnapshot, orderBy, query, where } from "firebase/firestore";
import "./notificationBubble/notificationBubble.scss";

// Caché de las empresas del selector: cada página monta su propio Navbar, así
// que la lectura de Firestore se hace UNA sola vez por sesión y las
// navegaciones posteriores pintan el chip de forma síncrona (sin parpadeo).
// (sufijo .v3: pasa de una empresa a la lista completa)
const EMPRESAS_CACHE_KEY = 'navbarEmpresas.v3';
// Empresa elegida en el desplegable (persistente entre sesiones, por usuario).
const EMPRESA_SELECCIONADA_KEY = 'navbarEmpresaSeleccionada';
let empresasCache = null;

const leerCacheEmpresas = (serviceId) => {
  if (empresasCache?.key === serviceId) return empresasCache.empresas;
  try {
    const raw = sessionStorage.getItem(EMPRESAS_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.key === serviceId && Array.isArray(parsed.empresas)) {
        empresasCache = parsed;
        return parsed.empresas;
      }
    }
  } catch (e) {
    // Caché corrupta: se ignora y se vuelve a consultar.
  }
  return null;
};

const guardarCacheEmpresas = (serviceId, empresas) => {
  empresasCache = { key: serviceId, empresas };
  try {
    sessionStorage.setItem(EMPRESAS_CACHE_KEY, JSON.stringify(empresasCache));
  } catch (e) {
    // sessionStorage lleno/bloqueado: la caché en memoria sigue valiendo.
  }
};

const leerSeleccion = (email) => {
  try {
    const raw = localStorage.getItem(EMPRESA_SELECCIONADA_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.[email] || null;
  } catch (e) {
    return null;
  }
};

const guardarSeleccion = (email, cod) => {
  try {
    const raw = localStorage.getItem(EMPRESA_SELECCIONADA_KEY);
    const parsed = (raw && JSON.parse(raw)) || {};
    parsed[email] = cod;
    localStorage.setItem(EMPRESA_SELECCIONADA_KEY, JSON.stringify(parsed));
  } catch (e) {
    // Sin persistencia: la elección vale solo para esta página.
  }
};

/**
 * Componente de la barra de navegación superior.
 */
const Navbar = () => {
  const { currentUser, currentService, adminData } = useContext(AuthContext);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadFormNotifications, setUnreadFormNotifications] = useState(0);
  const navigate = useNavigate();

  // Empresa mostrada en el chip: se elige en el desplegable entre todas las
  // empresas activas del servicio. Por defecto, la primera asignada al admin.
  // Es solo identificativa: no cambia el scope de datos del usuario.
  const serviceId = currentService?.id || null;
  const emailUsuario = currentUser?.email || '';
  const [empresas, setEmpresas] = useState(() => (serviceId ? leerCacheEmpresas(serviceId) || [] : []));
  const [codSeleccionado, setCodSeleccionado] = useState(() => leerSeleccion(emailUsuario));
  const [menuEmpresaAnchor, setMenuEmpresaAnchor] = useState(null);

  const codPorDefecto = adminData?.empresasAsignadas?.[0];
  const empresa = empresas.find((e) => e.codEmpresa === codSeleccionado)
    || empresas.find((e) => e.codEmpresa === codPorDefecto)
    || null;

  const { dispatch: sidebarDispatch } = useSidebarState();

  useEffect(() => {
    setCodSeleccionado(leerSeleccion(emailUsuario));
  }, [emailUsuario]);

  useEffect(() => {
    if (!serviceId) {
      setEmpresas([]);
      return undefined;
    }
    const cacheadas = leerCacheEmpresas(serviceId);
    if (cacheadas) {
      setEmpresas(cacheadas);
      return undefined;
    }
    let cancelado = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'servicios', serviceId, 'empresas'));
        // Solo los campos que pinta el chip (serializable para sessionStorage).
        const lista = snap.docs
          .map((d) => d.data() || {})
          .filter((data) => data.codEmpresa && data.borrado !== true && data.activa !== false)
          .map((data) => ({
            nombre: data.nombre || '',
            codEmpresa: String(data.codEmpresa),
            cif: data.cif || '',
          }))
          .sort((a, b) => a.codEmpresa.localeCompare(b.codEmpresa));
        guardarCacheEmpresas(serviceId, lista);
        if (!cancelado) setEmpresas(lista);
      } catch (e) {
        // Sin chip si la lectura falla; no bloquea el navbar.
      }
    })();
    return () => { cancelado = true; };
  }, [serviceId]);

  const handleSeleccionarEmpresa = (cod) => {
    setCodSeleccionado(cod);
    guardarSeleccion(emailUsuario, cod);
    setMenuEmpresaAnchor(null);
  };

  // Contador en tiempo real de notificaciones del dashboard sin leer (campana).
  useEffect(() => {
    if (!currentUser?.email || !currentService?.id) return undefined;

    const q = query(
      collection(db, "servicios", currentService.id, "notificaciones_dashboard"),
      where("destinatario", "in", [currentUser.email, "todos"]),
      where("leida", "==", false),
      orderBy("fecha", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUnreadNotifications(snapshot.docs.length);
    }, () => {
      // Sin contador si la suscripción falla; no bloquea el navbar.
    });

    return () => unsubscribe();
  }, [currentUser?.email, currentService?.id]);

  // Contador en tiempo real de notificaciones de formularios sin leer (buzón).
  useEffect(() => {
    if (!currentUser?.email || !currentService?.id) return undefined;

    const q = query(
      collection(db, "servicios", currentService.id, "notificaciones_formularios"),
      where("destinatario", "in", [currentUser.email, "todos"]),
      where("leida", "==", false)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUnreadFormNotifications(snapshot.docs.length);
    }, () => {
      // Sin contador si la suscripción falla; no bloquea el navbar.
    });

    return () => unsubscribe();
  }, [currentUser?.email, currentService?.id]);

  const handleUserClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleToggleSidebar = () => {
    sidebarDispatch(toggleSidebar());
  };

  return (
    <div className="navbar">
      <div className="wrapper">
        <div className="item" onClick={handleToggleSidebar}>
          <MenuIcon className="iconMenu" />
        </div>
        <div className="items">
          {empresas.length > 0 && (
            <>
              <div
                className="empresaChip"
                title="Seleccionar empresa"
                onClick={(e) => setMenuEmpresaAnchor(e.currentTarget)}
              >
                <BusinessIcon className="empresaIcon" />
                <div className="empresaTexto">
                  <span className="empresaNombre">{empresa ? empresa.nombre : 'Seleccionar empresa'}</span>
                  {empresa && (
                    <span className="empresaDetalle">
                      {empresa.codEmpresa}{empresa.cif ? ` - ${empresa.cif}` : ''}
                    </span>
                  )}
                </div>
                <ArrowDropDownIcon className="empresaFlecha" />
              </div>
              <Menu
                anchorEl={menuEmpresaAnchor}
                open={Boolean(menuEmpresaAnchor)}
                onClose={() => setMenuEmpresaAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {empresas.map((e) => (
                  <MenuItem
                    key={e.codEmpresa}
                    selected={empresa?.codEmpresa === e.codEmpresa}
                    onClick={() => handleSeleccionarEmpresa(e.codEmpresa)}
                  >
                    <ListItemText
                      primary={e.nombre}
                      secondary={`${e.codEmpresa}${e.cif ? ` - ${e.cif}` : ''}`}
                      primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                      secondaryTypographyProps={{ fontSize: 11 }}
                    />
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          <div
            className="item"
            title="Notificaciones"
            onClick={() => navigate('/notificaciones-internas')}
            style={{ cursor: 'pointer' }}
          >
            <NotificationsNoneOutlinedIcon className="icon" />
            {unreadNotifications > 0 && (
              <div className="counter">
                {unreadNotifications > 99 ? "99+" : unreadNotifications}
              </div>
            )}
          </div>
          <div
            className="item"
            title="Notificaciones de formularios"
            onClick={() => navigate('/notificaciones-internas')}
            style={{ cursor: 'pointer' }}
          >
            <MailOutlineIcon className="icon" />
            {unreadFormNotifications > 0 && (
              <div className="counter2">
                {unreadFormNotifications > 99 ? "99+" : unreadFormNotifications}
              </div>
            )}
          </div>
          <div className="item">
            {adminData && adminData.foto ? (
              <img src={adminData.foto} alt="Admin" className="avatar" />
            ) : (
              <PersonIcon className="avatar" />
            )}
            <p className="user" onClick={handleUserClick}>{adminData?.nombre ? `${adminData.nombre}${adminData.apellido ? ' ' + adminData.apellido : ''}` : (currentUser?.email || 'Usuario')}</p>
          </div>
          {isDropdownOpen && <UserDropdown onClose={() => setIsDropdownOpen(false)} />}
        </div>
      </div>
    </div>
  );
};

export default Navbar;
